import React from "react";
import { Params } from "react-router-dom";
import { tldsList } from "../constants/constants";
import { capitalize } from "../utils/string";

export const addKeyInArray = (data: any, keyName: string = "id") => {
  // TODO: _id Remove later
  return data?.map(
    (el: { [key: string]: any; id?: string; _id?: string }, idx: number) => ({
      ...el,
      key: el[keyName || "id"] || el?._id || `${idx}`,
    })
  );
};

export const selectSearchOption = (
  input: string,
  option?: { label: string; value: string | number | boolean }
) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

export const selectSearchMultiLabelOption = (
  input: string,
  option?: {
    label: React.ReactNode;
    title?: string;
    options?: { label: React.ReactNode; value: string | number }[];
  }
) => {
  const extractText = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return node.toString();
    if (React.isValidElement(node)) return extractText(node.props.children);
    if (Array.isArray(node)) return node.map(extractText).join(" ");
    return "";
  };

  // Extract text from the group label
  const labelText = extractText(option?.label);
  // Extract text from nested options, if available
  const optionTexts =
    option?.options?.map((opt) => extractText(opt.label)).join(" ") || "";

  const searchText = input.toLowerCase();
  return (
    labelText.toLowerCase().includes(searchText) ||
    optionTexts.toLowerCase().includes(searchText)
  );
};

export const selectArrayOption = (
  data: any[],
  label: string,
  value: string | number,
  isCapitalLabel?: boolean
) => {
  return data?.map((el) => ({
    label: isCapitalLabel ? capitalize(el[label]) : el[label],
    value: typeof value === "number" ? Number(el[value]) : el[value] || null,
  }));
};

export const selectArrayOptionWithAll = (
  data: any[],
  label: string,
  value: string | number,
  isCapitalLabel?: boolean
) => {
  const formattedData = data?.map((el) => ({
    label: isCapitalLabel ? capitalize(el[label]) : el[label],
    value: typeof value === "number" ? Number(el[value]) : el[value] || null,
  }));

  return [{ label: "All", value: "all" }, ...formattedData];
};

export const selectStringOption = (
  data: string[],
  option?: "number" | "string" | "boolean",
  isCapitalLabel?: boolean
) => {
  return data.map((el) => ({
    label: isCapitalLabel ? underscoreToCapitalizeString(el) : el,
    value: option === "number" ? Number(el) : el,
  }));
};

// generate back link for back button
export const backLink = (str: string, backCount: number = 1) => {
  const pathArray = str.split("/");
  return pathArray.slice(0, pathArray.length - backCount).join("/");
};

// url validation for antd form input
export const urlValidation = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  const urlRegex =
    /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;

  if (!urlRegex.test(value)) {
    return Promise.reject(new Error("Please enter a valid URL"));
  }
};

export const removeHttps = (url?: string) => {
  return url?.startsWith("https://") ? url.slice(8) : url;
};

// skip for get limited data (Pagination)
export const skipData = (page: number, limit: number) => {
  return limit * (page - 1);
};

// convert underscore to real string with capitalize
export const underscoreToCapitalizeString = (
  str: string,
  smallLetter?: boolean
) => {
  if (smallLetter) return str?.split("_")?.join(" ");
  return capitalize(str?.split("_")?.join(" "));
};

export const formatExtensions = (extensions: string[]): string => {
  return extensions.map((ext) => `.${ext}`).join(", ");
};

/**
 * Removes the parameter value from the end of the pathname.
 *
 * @param pathname - The full pathname string (e.g., "/path/to/resource/param").
 * @param params - An object containing route parameters (e.g., { key: "param" }).
 * @returns The sanitized pathname without the parameter value at the end.
 */
export const getSanitizedPathname = (
  pathname: string,
  params: Readonly<Params<string>>
): string => {
  const paramValues = Object.values(params);

  const pathParts = pathname.split("/");
  const lastSegment = pathParts[pathParts.length - 1];

  if (paramValues.includes(lastSegment)) {
    return pathParts.slice(0, -2).join("/");
  }

  return pathname;
};

export const convertToSnakeCase = (text: string, snakeCase = "_") => {
  return text?.split(" ")?.join(snakeCase)?.toLowerCase();
};

export function hexToRgba(hex: string, alpha = 0.198) {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function extractTLD(email: string) {
  const match = email.match(/\.([a-zA-Z]{2,10})$/);
  return match ? match[1].toUpperCase() : null;
}

export function isValidTLD(email: string) {
  const tld = extractTLD(email);
  return tld ? tldsList.includes(tld) : false;
}

export function remove880(phoneNumber?: string | null) {
  if (phoneNumber?.startsWith("+880")) {
    return phoneNumber.slice(4);
  }
  return phoneNumber;
}

export function formatPhoneNumber(number: string) {
  if (number?.startsWith("+88")) {
    return number;
  } else {
    return "+88" + number;
  }
}

export function calculateDateDifference(startDate: string, endDate: string) {
  const start: any = new Date(startDate);
  const end: any = new Date(endDate);

  // Calculate difference in milliseconds
  const differenceInTime = end - start;

  // Convert milliseconds to days
  const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

  return differenceInDays;
}

export const checkReload = () => {
  const entries = performance.getEntriesByType("navigation");
  if (
    entries.length > 0 &&
    (entries[0] as PerformanceNavigationTiming).type === "reload"
  ) {
    return true;
  }
  return false;
};

export function formatToTwoDecimals(value: number): string {
  return value?.toFixed(2);
}

export function emailToShortCode(email: string): string {
  const [username, domain] = email.split("@");
  if (!username || !domain) return "";

  const start = username.slice(0, 2);

  const domainParts = domain.split(".");
  const domainStart = domainParts[0].slice(-2); // 'il' from 'gmail'
  const tld = domainParts.slice(1).join(".");

  return `${start}***${domainStart}.${tld}`;
}

export function formatSouthAsianNumber(number: number | string = 0) {
  const numStr = number.toString();
  const lastThree = numStr.slice(-3);
  const otherNumbers = numStr.slice(0, -3);

  if (otherNumbers === "") return lastThree;

  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
}

export function formatSouthAsianWithDecimals(value: number): string {
  const [intPart, decimalPart] = formatToTwoDecimals(value).split(".");
  return `${formatSouthAsianNumber(intPart)}.${decimalPart}`;
}

type RedirectTab = "self" | "new";

export function redirectToUrl(url: string, tab: RedirectTab = "self") {
  const topUrlLink = document.createElement("a");
  topUrlLink.setAttribute("href", url);
  if (tab === "new") {
    topUrlLink.setAttribute("target", "_blank");
  }
  topUrlLink.click();
}
